import {
    compactWidgets,
    hasDashboardWidgetRecords,
    normalizePlacement,
    overlaps,
    samePlacement,
    toPlacement,
    type Placement
} from '../../utils/dashboard';
import type { DashboardWidget } from './Dashboard';

type Preset = { id: string; label: string; description: string; placements: Record<string, Placement> };

export const DASHBOARD_LAYOUT_PRESETS: Preset[] = [
    {
        id: 'balanced',
        label: 'Balanced',
        description:
            'Three panels across the top, wide Holding and Diverting lists below, and shallow Emergency and Go-around lists at the bottom.',
        placements: {
            taxiTimes: { x: 0, y: 0, w: 4, h: 6 },
            positionUpdated: { x: 4, y: 0, w: 5, h: 6 },
            flightStatsChart: { x: 9, y: 0, w: 3, h: 6 },
            holding: { x: 0, y: 6, w: 6, h: 4 },
            diverting: { x: 6, y: 6, w: 6, h: 4 },
            emergency: { x: 0, y: 10, w: 6, h: 3 },
            goAround: { x: 6, y: 10, w: 6, h: 3 }
        }
    },
    {
        id: 'monitor-sidebar',
        label: 'Monitor sidebar',
        description: 'A full-height monitor chart on the right, with six flight lists arranged in two columns on the left.',
        placements: {
            holding: { x: 0, y: 0, w: 4, h: 4 },
            diverting: { x: 4, y: 0, w: 4, h: 4 },
            emergency: { x: 0, y: 4, w: 4, h: 4 },
            goAround: { x: 4, y: 4, w: 4, h: 4 },
            taxiTimes: { x: 0, y: 8, w: 4, h: 4 },
            positionUpdated: { x: 4, y: 8, w: 4, h: 4 },
            flightStatsChart: { x: 8, y: 0, w: 4, h: 12 }
        }
    },
    {
        id: 'wide-tables',
        label: 'Wide tables',
        description: 'Six flight lists in two wide columns, followed by a full-width monitor chart.',
        placements: {
            holding: { x: 0, y: 0, w: 6, h: 4 },
            diverting: { x: 6, y: 0, w: 6, h: 4 },
            emergency: { x: 0, y: 4, w: 6, h: 4 },
            goAround: { x: 6, y: 4, w: 6, h: 4 },
            taxiTimes: { x: 0, y: 8, w: 6, h: 6 },
            positionUpdated: { x: 6, y: 8, w: 6, h: 6 },
            flightStatsChart: { x: 0, y: 14, w: 12, h: 6 }
        }
    }
];

export const DEFAULT_DASHBOARD_LAYOUT = 'balanced';
/** Preserve visibility and widget metadata; only replace layout coordinates and sizes. */
export function applyDashboardLayout(widgets: DashboardWidget[], presetId: string): DashboardWidget[] {
    const preset = DASHBOARD_LAYOUT_PRESETS.find((item) => item.id === presetId);
    if (!preset) return widgets;

    const next = widgets.map((widget) => {
        const position = preset.placements[widget.id];
        return position
            ? {
                  ...widget,
                  layout: {
                      ...widget.layout,
                      ...normalizePlacement(position, widget.layout),
                      i: widget.id
                  }
              }
            : { ...widget, layout: { ...widget.layout } };
    });

    // Complete presets retain their geometry; partially enabled presets fill gaps.
    return next.every((widget) => widget.enabled) ? next : compactWidgets(next, true);
}

/** Infer the selection from saved geometry; manual dragging is shown as Custom. */
export function detectDashboardLayout(widgets: DashboardWidget[]): string {
    if (!widgets.some((widget) => widget.enabled)) return 'custom';
    return (
        DASHBOARD_LAYOUT_PRESETS.find((preset) => {
            const expected = applyDashboardLayout(widgets, preset.id);
            return widgets.every((widget, index) => !widget.enabled || samePlacement(widget.layout, expected[index].layout));
        })?.id ?? 'custom'
    );
}

/** Close vertical gaps while preserving each base layout's columns and panel sizes.
 * Positions are temporary: they must never replace saved layout coordinates.
 */
export function compactVisibleDashboardWidgets(widgets: DashboardWidget[]): DashboardWidget[] {
    return compactWidgets(widgets.filter((widget) => widget.enabled));
}

export function getVisibleDashboardWidgets(widgets: DashboardWidget[], counts: readonly number[] | null): DashboardWidget[] {
    return compactVisibleDashboardWidgets(
        widgets.map((widget) => ({
            ...widget,
            enabled: widget.enabled && hasDashboardWidgetRecords(widget.id, counts)
        }))
    );
}

/** Commit an intentional edit, then place omitted widgets around the edited panels.
 * Passive grid compaction must not call this: it would replace the base layout.
 */
export function mergeEditedDashboardLayout(
    widgets: DashboardWidget[],
    nextLayout: readonly (Placement & { i: string })[]
): DashboardWidget[] {
    const edits = new Map(nextLayout.map((item) => [item.i, item]));
    const placed: Placement[] = [];
    const result = new Map<string, DashboardWidget>();
    const ordered = [...widgets.filter((widget) => edits.has(widget.id)), ...widgets.filter((widget) => !edits.has(widget.id))];
    for (const widget of ordered) {
        const edit = edits.get(widget.id);
        const layout = edit ? { ...widget.layout, ...toPlacement(edit) } : { ...widget.layout };
        if (!edit) {
            while (placed.some((other) => overlaps(layout, other))) {
                layout.y = Math.max(...placed.filter((other) => overlaps(layout, other)).map((other) => other.y + other.h));
            }
        }
        placed.push(layout);
        result.set(widget.id, { ...widget, layout });
    }
    return widgets.map((widget) => result.get(widget.id)!);
}
